/**
 * Temporary screen-scoped design tokens for the Sign Requests **filters, views
 * and tags** layer, traced from `Sign Requests - Filters & Tags.dc.html`.
 *
 * Sits beside `SignRequests.tokens.ts` rather than inside it: that file mirrors
 * the base screen, this one the toolbar, the active-filter chips, the filter
 * drawer, the table columns and the tag surfaces added on top of it. Dialog
 * chrome, button fills and field geometry are not restated here — those come
 * from `AppModal`, the global button theme and `FormField.tokens`.
 */

import {
  button,
  contentCard,
  formField,
  menu,
  notification,
  signer,
  white,
} from "theme/colors";
import { signRequestsStatusTokens } from "./SignRequests.tokens";

/**
 * The tablet reference (768) carries a Tags column the narrowest `sm` widths
 * have no room for. MUI has no breakpoint between 600 and 900, so the column is
 * switched on by a raw query — bounded above, because a raw query is emitted
 * after the breakpoint values and would otherwise override `lg` / `xl` too.
 */
export const TABLET_WIDE_QUERY =
  "@media (min-width:720px) and (max-width:1199.95px)";

export const signRequestsFiltersTokens = {
  /** Search field plus the Sort / Views / Filters triggers. */
  toolbar: {
    gap: "10px",
    marginBottom: "14px",
    height: 44,
    radius: "10px",
    paddingX: "14px",
    bg: white,
    border: formField.borderWarm,
    hoverBorder: "#CFC9BA",
    focusBorder: signer.orange.main,
    searchGap: "10px",
    searchIconSize: 17,
    searchIconColor: signer.text.faint,
    inputFontSize: "13.5px",
    triggerGap: "8px",
    triggerFontSize: "13px",
    triggerFontWeight: 600,
    triggerColor: signer.text.secondary,
    triggerIconSize: 16,
    triggerIconColor: signer.text.subtitle,
    caretSize: 12,
    caretColor: signer.text.faint,
    viewsMaxWidth: 230,
    /** "Filters" while at least one filter is applied. */
    activeBg: signer.orange.tint,
    activeBorder: signer.orange.border,
    activeColor: signer.orange.dark,
    filtersFontWeight: 700,
    countSize: 20,
    countPadding: "0 6px",
    countBg: signer.orange.main,
    countColor: white,
    countFontSize: "11px",
    countFontWeight: 800,
  },

  /** Sort and Views dropdowns. The card itself is the global menu theme. */
  dropdown: {
    sortWidth: 220,
    viewsWidth: 300,
    checkSize: 16,
    checkColor: signer.orange.main,
    itemGap: "10px",
    itemFontSize: "13px",
    activeFontWeight: 700,
    restFontWeight: 500,
    itemColor: signer.ink,
    sectionLabelPadding: "8px 11px 6px",
    sectionLabelFontSize: "11px",
    sectionLabelFontWeight: 700,
    sectionLabelSpacing: ".06em",
    // Darker than the mock's #9B96AE, which is 2.7:1 on white — small uppercase
    // text needs 4.5:1, and `meta` is the nearest palette grey that clears it.
    sectionLabelColor: signer.text.meta,
    kebabSize: 30,
    kebabRadius: "7px",
    kebabColor: signer.text.subtitle,
    kebabHoverBg: "#EAE6F3",
    kebabHoverColor: signer.ink,
    divider: signer.border.light,
    saveColor: signer.orange.main,
    saveHoverBg: signer.orange.tint,
    saveFontWeight: 700,
    manageFontSize: "12.5px",
    manageFontWeight: 600,
    manageColor: signer.text.meta,
    managePadding: "8px 11px 9px 37px",
    /** Inline "Delete this view?" confirm. */
    confirmBg: notification.error.bg,
    confirmColor: notification.error.title,
    confirmPadding: "10px 11px",
    confirmRadius: "7px",
    confirmGap: "8px",
    confirmFontSize: "12.5px",
    dangerColor: menu.dangerText,
  },

  /** "Filters from a shared link" notice. */
  sharedBanner: {
    gap: "12px",
    padding: "11px 14px",
    marginBottom: "12px",
    radius: "10px",
    bg: signRequestsStatusTokens.progress.bg,
    border: "#CFE0F6",
    iconSize: 17,
    iconColor: signRequestsStatusTokens.progress.color,
    fontSize: "13px",
    color: "#1B4A80",
    strongColor: "#123B69",
    actionColor: signRequestsStatusTokens.progress.color,
    actionBorder: "#9FC1EC",
    dismissSize: 30,
    dismissRadius: "7px",
    dismissIconSize: 14,
    dismissHoverBg: "#D7E6F8",
  },

  /** Applied-filter chips under the toolbar. */
  chips: {
    gap: "8px",
    marginBottom: "14px",
    height: 32,
    padding: "0 4px 0 12px",
    innerGap: "4px",
    radius: "999px",
    bg: white,
    border: signer.border.main,
    fontSize: "12.5px",
    color: signer.text.secondary,
    keyColor: signer.ink,
    keyFontWeight: 700,
    removeSize: 24,
    removeIconSize: 12,
    removeColor: signer.text.subtitle,
    removeHoverBg: signer.surface.muted,
    removeHoverColor: signer.ink,
  },

  /** Column header row and the cells the new columns add to a request row. */
  table: {
    gap: "12px",
    /** Subject | Status | Expires | actions. */
    gridNarrow: "minmax(0,1fr) 150px 92px 68px",
    /** Subject | Status | Tags | Expires | actions — the 768 reference. */
    gridTablet: "minmax(0,1fr) 150px 140px 92px 68px",
    /** Subject | Signers | Status | Tags | Expires | actions. */
    gridLaptop: "minmax(0,1fr) 72px 140px 196px 90px 68px",
    /** The 1440 reference, every column. */
    gridDesktop: "minmax(0,1fr) 72px 112px 140px 196px 86px 90px 68px",
    /**
     * The same tracks without Tags, for lists read from the folder endpoint —
     * it carries no tags, so the column would be a row of dashes.
     */
    gridLaptopNoTags: "minmax(0,1fr) 72px 140px 90px 68px",
    gridDesktopNoTags: "minmax(0,1fr) 72px 112px 140px 86px 90px 68px",
    headPadding: "12px 20px",
    headBg: signer.surface.hover,
    headBorder: contentCard.border,
    headFontSize: "11px",
    headFontWeight: 700,
    headLetterSpacing: ".06em",
    // See `dropdown.sectionLabelColor` — same contrast adjustment.
    headColor: signer.text.meta,
    rowPadding: "14px 20px",
    leadGap: "12px",
    iconTileSize: 36,
    iconTileRadius: "8px",
    iconSize: 17,
    titleFontSize: "13.5px",
    subFontSize: "11.5px",
    avatarSize: 28,
    avatarFontSize: "10px",
    chipPadding: "5px 10px",
    senderFontSize: "13px",
    dateFontSize: "12.5px",
    cellColor: signer.text.secondary,
    soonFontSize: "11px",
    soonFontWeight: 700,
    soonColor: notification.warning.icon,
    /** Days ahead inside which an open request reads as expiring soon. */
    soonWindowDays: 7,
    emptyValue: "—",
    emptyColor: signer.text.faint,
    actionsGap: "4px",
  },

  /** Tag chips in the Tags column and the per-row tag trigger. */
  tags: {
    gap: "5px",
    height: 22,
    padding: "0 8px",
    radius: "6px",
    fontSize: "11px",
    fontWeight: 700,
    maxWidth: 88,
    /** Chips drawn before the rest collapse into "+N". */
    maxVisible: 3,
    maxVisibleTablet: 2,
    moreBg: signer.surface.pill,
    moreColor: signer.text.meta,
    morePadding: "0 7px",
    /** A request carries at most this many tags. */
    limit: 10,
    triggerSize: 32,
    triggerRadius: "8px",
    triggerIconSize: 16,
    triggerBorder: signer.border.main,
    triggerColor: signer.text.meta,
    triggerActiveBorder: signer.orange.main,
    triggerActiveBg: signer.orange.tint,
    triggerActiveColor: signer.orange.main,
    dotSize: 9,
  },

  /** Popover opened from a row's tag trigger. */
  picker: {
    width: 290,
    radius: "12px",
    border: menu.border,
    shadow: menu.shadow,
    searchPadding: "10px",
    divider: signer.border.light,
    listMaxHeight: 240,
    listPadding: "5px",
    itemGap: "10px",
    itemPadding: "2px 9px 2px 2px",
    itemRadius: "7px",
    itemHoverBg: signer.surface.subtle,
    itemFontSize: "13px",
    itemFontWeight: 600,
    hintPadding: "14px 14px 4px",
    hintFontSize: "12.5px",
    hintColor: signer.text.meta,
    hintLineHeight: 1.55,
    createPadding: "11px 14px",
    createGap: "9px",
    createIconSize: 15,
    createFontSize: "13px",
    createFontWeight: 700,
    createColor: signer.orange.main,
    createHoverBg: signer.orange.tint,
    limitGap: "8px",
    limitPadding: "10px 14px",
    limitBg: notification.warning.bg,
    limitBorder: notification.warning.border,
    limitColor: notification.warning.text,
    limitFontSize: "12px",
    limitFontWeight: 600,
    limitIconSize: 14,
    footerPadding: "9px 14px",
    footerBg: signer.surface.hover,
    countFontSize: "11.5px",
    countColor: signer.text.meta,
    linkFontSize: "12px",
    linkFontWeight: 700,
    linkColor: signer.text.meta,
  },

  /**
   * The row under the toolbar: one segmented pill per applied filter, then
   * "Add filter", "All filters", and — pushed right — "Save view" / "Clear all".
   */
  filterBar: {
    gap: "8px",
    marginBottom: "14px",
    height: 34,
    radius: "8px",
    bg: white,
    border: signer.border.main,
    openBorder: signer.orange.main,
    shadow: "0 1px 2px rgba(33,21,71,.04)",
    divider: contentCard.border,
    fontSize: "12.5px",
    fontWeight: 700,
    labelGap: "7px",
    labelPadding: "0 10px",
    labelBg: signer.surface.hover,
    labelColor: signer.text.secondary,
    iconSize: 15,
    iconColor: signer.text.subtitle,
    opPadding: "0 9px",
    opFontSize: "12px",
    opColor: signer.text.meta,
    valueGap: "6px",
    valuePadding: "0 10px",
    valueMaxWidth: 240,
    valueColor: signer.ink,
    valueHoverBg: signer.surface.subtle,
    valueOpenBg: signer.orange.tint,
    partHeight: 22,
    partGap: "5px",
    partRadius: "5px",
    partTagPadding: "0 7px",
    partDotSize: 7,
    moreFontSize: "12px",
    moreColor: signer.text.meta,
    emptyColor: formField.placeholder,
    removeWidth: 30,
    removeIconSize: 11,
    removeColor: signer.text.meta,
    removeHoverBg: notification.error.bg,
    removeHoverColor: menu.dangerText,
    addGap: "6px",
    addPadding: "0 11px",
    addBorder: signer.border.strong,
    addColor: signer.text.secondary,
    addHoverColor: signer.orange.main,
    addIconSize: 12,
    addMenuWidth: 220,
    linkGap: "7px",
    linkPadding: "0 10px",
    linkColor: signer.text.meta,
    linkHoverBg: signer.border.light,
    linkHoverColor: signer.ink,
    linkIconSize: 15,
    saveColor: signer.text.secondary,
    saveIconSize: 14,
  },

  /**
   * The editor for one filter — the same block inside a pill's popover and in
   * the right-hand pane of the "All filters" drawer.
   */
  filterPanel: {
    popoverWidth: 320,
    popoverRadius: "12px",
    popoverBorder: menu.border,
    popoverShadow: "0 16px 40px rgba(33,21,71,.18)",
    headPadding: "14px 16px 10px",
    titleFontSize: "13.5px",
    titleFontWeight: 800,
    titleColor: signer.ink,
    bodyPadding: "0 8px 10px",
    rowGap: "2px",
    optionGap: "10px",
    optionPadding: "2px 8px 2px 0",
    optionRadius: "7px",
    optionHoverBg: signer.surface.subtle,
    optionCheckedBg: signer.orange.faint,
    optionFontSize: "13px",
    optionColor: signer.ink,
    optionCheckedWeight: 700,
    hintFontSize: "11.5px",
    hintColor: signer.text.meta,
    statusChipGap: "7px",
    statusChipPadding: "3px 9px",
    statusDotSize: 7,
    statusFontSize: "12px",
    customPadding: "6px 8px 2px 34px",
    dateBorder: formField.border,
    dateHoverBorder: signer.border.strong,
    datePromptColor: formField.placeholder,
    dateIconSize: 16,
    dateIconGap: "9px",
    dateIconColor: signer.text.subtitle,
    dateCaretSize: 12,
    fieldGap: "8px",
    fieldPadding: "0 8px",
    noteFontSize: "11.5px",
    noteColor: signer.text.meta,
    noteLineHeight: 1.5,
    listMaxHeight: 236,
    tagHeight: 22,
    tagPadding: "0 8px",
    tagRadius: "6px",
    tagFontSize: "12px",
    avatarSize: 24,
    avatarColor: white,
    avatarFontSize: "9.5px",
    emptyPadding: "12px",
    emptyRadius: "8px",
    emptyBg: signer.surface.subtle,
    emptyFontSize: "12.5px",
    emptyColor: signer.text.meta,
    emptyLineHeight: 1.55,
    emptyLinkColor: signer.orange.main,
    noMatchPadding: "10px 8px",
    footGap: "8px",
    footPadding: "10px 12px",
    footBorder: signer.border.light,
    footBg: signer.surface.hover,
  },

  /** Right-hand "All filters" drawer: a category rail beside one filter panel. */
  drawer: {
    width: 520,
    widthTablet: 460,
    shadow: "-12px 0 40px rgba(33,21,71,.16)",
    scrim: "rgba(33,21,71,.28)",
    headPadding: "16px 16px 16px 22px",
    headGap: "12px",
    headBorder: contentCard.border,
    titleFontSize: "17px",
    titleFontWeight: 800,
    titleColor: signer.ink,
    subtitleFontSize: "12px",
    subtitleColor: signer.text.meta,
    closeSize: 34,
    closeRadius: "8px",
    closeIconSize: 16,
    closeColor: signer.text.meta,
    closeHoverBg: signer.surface.muted,
    railWidth: 156,
    railWidthPhone: 124,
    railBg: signer.surface.hover,
    railBorder: contentCard.border,
    railPadding: "10px 8px",
    railGap: "2px",
    tabGap: "8px",
    tabPadding: "10px 10px 10px 12px",
    tabRadius: "8px",
    tabFontSize: "13px",
    tabColor: signer.text.secondary,
    tabActiveColor: signer.ink,
    tabActiveBg: white,
    tabActiveShadow: "0 1px 3px rgba(33,21,71,.08)",
    paneHeadPadding: "16px 16px 10px 18px",
    paneTitleFontSize: "14.5px",
    panePadding: "0 10px 16px",
    optionColor: signer.ink,
    footPadding: "14px 18px",
    footGap: "10px",
    footBorder: contentCard.border,
    footShadow: "0 -6px 18px rgba(33,21,71,.05)",
  },

  /** Body content of the views and tags dialogs (the shell is `AppModal`). */
  dialogs: {
    rowGap: "12px",
    rowPadding: "12px 0",
    rowBorder: signer.border.light,
    nameFontSize: "13.5px",
    nameFontWeight: 700,
    nameColor: signer.ink,
    metaFontSize: "12px",
    metaColor: signer.text.meta,
    actionSize: 32,
    actionRadius: "7px",
    actionIconSize: 15,
    actionColor: signer.text.meta,
    actionHoverBg: signer.surface.muted,
    dangerColor: menu.dangerText,
    dangerHoverBg: menu.dangerHoverBg,
    summaryLabelFontSize: "12.5px",
    summaryLabelFontWeight: 700,
    summaryLabelColor: signer.text.secondary,
    summaryMarginTop: "16px",
    summaryGap: "6px",
    summaryPadding: "12px",
    summaryRadius: "9px",
    summaryBg: signer.surface.subtle,
    summaryChipHeight: 26,
    summaryChipPadding: "0 10px",
    noticeGap: "9px",
    noticePadding: "10px 12px",
    noticeRadius: "8px",
    noticeBg: signer.surface.muted,
    noticeColor: signer.text.secondary,
    noticeFontSize: "12.5px",
    editorGap: "12px",
    editorPadding: "14px 0",
    swatchGap: "8px",
    swatchSize: 28,
    swatchRing: "2px",
    swatchInset: "2px",
    dotSize: 10,
    emptyPadding: "40px 12px",
    emptyFontSize: "13.5px",
    emptyColor: signer.text.meta,
    emptyLineHeight: 1.6,
    confirmBorder: button.secondaryBorderWarm,
  },
} as const;

/**
 * Tag colours: the dot, the chip fill and the chip ink for each swatch, plus
 * `hex` — the preset the tags API stores (`#RRGGBB`) and the only form a
 * colour travels in. The eight presets are the backend's own list.
 *
 * Orange is a real orange here, not brand red: it is a data colour whose whole
 * job is to be told apart from Red, the same reason the decline-flow highlight
 * swatches it borrows are exempt from the orange-to-brand rule.
 */
export const signRequestsTagColors = {
  gray: {
    label: "Gray",
    hex: "#6B7280",
    dot: signer.text.subtitle,
    bg: signer.surface.pill,
    fg: "#4F4966",
  },
  red: {
    label: "Red",
    hex: "#DC2626",
    dot: notification.error.icon,
    bg: notification.error.bg,
    fg: notification.error.text,
  },
  orange: {
    label: "Orange",
    hex: "#EA580C",
    dot: signer.highlight.orange.main,
    bg: signer.highlight.orange.chipBg,
    fg: signer.highlight.orange.chipText,
  },
  yellow: {
    label: "Yellow",
    hex: "#CA8A04",
    dot: "#D49A1E",
    bg: "#FDF3E2",
    fg: "#7A560A",
  },
  green: {
    label: "Green",
    hex: "#16A34A",
    dot: notification.success.icon,
    bg: notification.success.bg,
    fg: notification.success.title,
  },
  teal: {
    label: "Teal",
    hex: "#0891B2",
    dot: signRequestsStatusTokens.approval.dot,
    bg: signRequestsStatusTokens.approval.bg,
    fg: signRequestsStatusTokens.approval.color,
  },
  blue: {
    label: "Blue",
    hex: "#2563EB",
    dot: signRequestsStatusTokens.progress.dot,
    bg: signRequestsStatusTokens.progress.bg,
    fg: signRequestsStatusTokens.progress.color,
  },
  purple: {
    label: "Purple",
    hex: "#9333EA",
    dot: signer.purple.main,
    bg: signer.purple.tint,
    fg: signer.purple.dark,
  },
} as const;

export type SignRequestsTagColor = keyof typeof signRequestsTagColors;

export const SIGN_REQUESTS_TAG_COLOR_KEYS = Object.keys(
  signRequestsTagColors,
) as SignRequestsTagColor[];

/** Used when a tag arrives with a colour outside the presets. */
export const DEFAULT_TAG_COLOR: SignRequestsTagColor = "blue";

export default signRequestsFiltersTokens;
